import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ShieldCheck, Facebook } from "lucide-react";
import { companyInfo, partnerLogos } from "../data";

const marqueeStyle = `
  @keyframes marquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  .animate-marquee-infinite {
    display: flex;
    width: max-content;
    animation: marquee 25s linear infinite;
  }
`;

export default function Footer() {
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const styleId = "marquee-style";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = marqueeStyle;
      document.head.appendChild(style);
    }
  }, []);

  const menuLinks = [
    { label: "Forside", href: "/" },
    { label: "Værksted", href: "/vaerkstedet" },
    { label: "Service & Reparation", href: "/service-og-reparation" },
    { label: "Booking", href: "/online-booking" },
    { label: "Om os", href: "/om-os" },
    { label: "Galleri", href: "/galleri" },
    { label: "Kontakt", href: "/kontakt" }
  ];

  const serviceLinks = [
    { label: "Serviceeftersyn", href: "/vaerkstedet/serviceeftersyn" },
    { label: "Klargøring til syn", href: "/vaerkstedet/klargoring-til-syn" },
    { label: "Fejlsøgning", href: "/vaerkstedet/fejlsogning" },
    { label: "Dækopbevaring", href: "/vaerkstedet/daekopbevaring" },
    { label: "Pladeværksted", href: "/vaerkstedet/pladevaerksted" },
    { label: "Olieskift/skylning af automatgearkasse", href: "/vaerkstedet/olieskift-skylning-af-automatgearkasse" }
  ];

  // Gentag logoer for at skabe en uendelig, glidende marquee-effekt
  const marqueeLogos = [...partnerLogos, ...partnerLogos, ...partnerLogos, ...partnerLogos, ...partnerLogos, ...partnerLogos];

  return (
    <>
      {/* Partner Slider Section (Godkendt & Certificeret) */}
      <div className="bg-slate-950 border-t border-b border-slate-800/60 py-10 overflow-hidden relative">
        
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-6 text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
            Godkendt &amp; Certificeret Partner
          </p>
        </div>

        {/* Slider Container med gradient-fade i siderne */}
        <div className="relative w-full overflow-hidden flex items-center">
          {/* Venstre Gradient Fade */}
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-slate-950 to-transparent z-10 pointer-events-none" />
          
          {/* Højre Gradient Fade */}
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none" />

          {/* Rullende spor */}
          <div className="animate-marquee-infinite hover:[animation-play-state:paused] flex gap-16 md:gap-24 items-center whitespace-nowrap">
            {marqueeLogos.map((logo, idx) => (
              <div key={`${logo.name}-${idx}`} className="inline-flex items-center justify-center shrink-0">
                <img
                  src={logo.src}
                  alt={logo.name}
                  className="h-10 md:h-12 w-auto object-contain brightness-0 invert opacity-50 hover:opacity-100 transition-all duration-300"
                  title={logo.name}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <footer className="relative border-t border-slate-900 bg-neutral-900 text-slate-400 text-sm">
      
      {/* Main Footer Content (4-kolonne layout inspireret af auto-marine.dk) */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          
          {/* Column 1: Identity & Contact */}
          <div className="space-y-6 text-left">
            <Link to="/" className="inline-block">
              <img
                alt="BRDR. LORENZEN. SKÆRBÆK ApS"
                className="h-7 w-auto object-contain brightness-100 filter"
                src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/d6fd363e-e86f-4923-a016-57b110ba59be.svg?t=1779874927921"
              />
            </Link>
            
            <p className="text-xs leading-relaxed text-slate-400">
              Klassisk, troværdigt og professionelt dansk autoværksted med lokal forankring og ærlig service. Etableret i 1976.
            </p>

            <ul className="space-y-3 text-xs">
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 shrink-0 text-white" />
                <div>
                  <p className="text-white">{companyInfo.address}</p>
                  <p className="text-slate-400">{companyInfo.zipCity}</p>
                </div>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 shrink-0 text-white" />
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="text-white hover:text-blue-500 transition-colors font-mono"
                >
                  {companyInfo.phoneFormatted}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 shrink-0 text-white" />
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-white hover:text-blue-500 transition-colors break-all"
                >
                  {companyInfo.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Column 2: Primary Menu */}
          <div className="space-y-6 text-left">
            <h3 className="text-xs font-bold uppercase tracking-widest text-white border-b border-slate-800 pb-2">
              MENU
            </h3>
            <ul className="space-y-2.5 text-xs">
              {menuLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="hover:text-blue-500 transition-colors uppercase tracking-wider font-medium"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Service Portfolio */}
          <div className="space-y-6 text-left">
            <h3 className="text-xs font-bold uppercase tracking-widest text-white border-b border-slate-800 pb-2">
              VÆRKSTEDSYDELSER
            </h3>
            <ul className="space-y-2.5 text-xs">
              {serviceLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="hover:text-blue-500 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Opening Hours */}
          <div className="space-y-6 text-left">
            <h3 className="text-xs font-bold uppercase tracking-widest text-white border-b border-slate-800 pb-2">
              ÅBNINGSTIDER
            </h3>
            
            <div className="space-y-2 text-xs">
              {companyInfo.openingHours.map((item) => (
                <div key={item.days} className="flex justify-between border-b border-slate-800/40 pb-1.5">
                  <span className="text-slate-400">{item.days}</span>
                  <span className="text-white font-medium font-mono">{item.hours}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Copyright & Legal Sub-Footer */}
      <div className="bg-slate-950 py-6 border-t border-slate-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-500">
          <div className="text-left">
            <p>&copy; {currentYear} {companyInfo.name}. CVR: {companyInfo.cvr}. Alle rettigheder forbeholdes.</p>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://www.facebook.com/profile.php?id=100057279183350"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-500 transition-colors"
              title="Følg os på Facebook"
            >
              <Facebook className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>

    </footer>
    </>
  );
}